import { Component } from '@theme/component';
import {
  debounce,
  hasOtherScrollLockClaimants,
  hasVisibleFocus,
  isClickedOutside,
  muteFocusRing,
  onAnimationEnd,
} from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Whether whatever opened the dialog was already carrying a focus ring, which is the one
   * case where the ring belongs back on it once the dialog closes.
   */
  #triggerWasRinged = false;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // Read now, not in the frame below: this is the last moment where the browser's own
    // answer still describes how the person reached the trigger rather than what they did
    // inside the panel.
    this.#triggerWasRinged = hasVisibleFocus(document.activeElement);

    this.#previousScrollY = getScrollTop();

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      document.documentElement.setAttribute('scroll-lock', '');

      dialog.showModal();

      // Focus the dialog itself, not the first control inside it. `showModal()`'s own
      // focusing steps land on the first focusable descendant, which in every panel here is
      // the close button — and focusing a control paints its focus ring: WebKit matches
      // `:focus-visible` on a programmatic focus that follows a tap, so the ring appeared on
      // open rather than on a deliberate keyboard move, drawn `outline-offset` outside a
      // button that sits in the panel's corner. Same reasoning and same fix as
      // assets/theme-drawer.js (BAS-272), which the drawers already took.
      //
      // Focusing the dialog is also what the ARIA practices ask for when the first control
      // is a dismiss button: a screen reader announces the panel by its own label instead of
      // reading "Close", and Tab still lands on that button first, ringed, for keyboard
      // users. `tabindex` is set here rather than in each panel's markup so every
      // dialog-component gets it, and re-set on every open because a Section Rendering morph
      // reconciles against server HTML that has no such attribute. `preventScroll` because
      // focus must not scroll anything while the panel is still sliding in.
      dialog.setAttribute('tabindex', '-1');
      dialog.focus({ preventScroll: true });

      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    if (!hasOtherScrollLockClaimants(dialog)) {
      document.documentElement.removeAttribute('scroll-lock');
    }
    scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    // `close()` hands focus back to whatever opened the dialog, and the browser rings it
    // whenever the keyboard was the last thing used: typing a search query is enough. So the
    // ring is muted unless the trigger was already wearing one when it opened the panel,
    // which is the only reader who needs it back to know where focus landed.
    if (!this.#triggerWasRinged) muteFocusRing(document.activeElement);

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else if (!hasOtherScrollLockClaimants(event.target)) {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true },
);
